import { useSwipeGesture } from "@base/model/hooks";
import { Funnel, useFunnel } from "@base/model/hooks/useFunnel";
import { ProgressBar } from "@base/ui/ProgressBar";
import { Spinner } from "@base/ui/Spinner/Spinner";
import { Button } from "@buildblockinc/buildblock-design-system";
import { DAILY_LOG_STEPS, STEP_TITLES } from "@features/write-daily-log/model/constants";
import type { DailyLogStep } from "@features/write-daily-log/model/constants";
import {
  canProceedToNextStep,
  findFirstBlockingStepIndex,
  getDailyLogRailStepStates,
  isSitePictureStepComplete,
} from "@features/write-daily-log/model/dailyLogStepNavigation";
import type { DailyLogForm } from "@features/write-daily-log/model/form";
import { useDailyLogImageController } from "@features/write-daily-log/model/useDailyLogImageController";
import dayjs from "dayjs";
import { ChevronLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { DailyLogStepRail } from "./DailyLogStepRail";
import { FixedBottomCTA } from "./FixedBottomCTA";
import { EquipmentStep } from "./steps/EquipmentStep";
import { MilestoneReportStep } from "./steps/MilestoneReportStep";
import { OverviewStep } from "./steps/OverviewStep";
import { SitePictureStep } from "./steps/SitePictureStep";
import { WorkforceStep } from "./steps/WorkforceStep";

interface WriteDailyLogFunnelProps {
  form: DailyLogForm;
  /** 업로드 이벤트를 현장 단위로 묶기 위한 컨텍스트. */
  projectId: string;
  /** create 모드에서 사용 중인 드래프트 식별자. edit 모드에는 없다. */
  draftId?: string;
  onNavigateBack: () => void;
  onSubmit: () => void;
  onStepChange?: (step: DailyLogStep) => void;
  isSaving?: boolean;
  lastUpdatedAt?: string | null;
  /** 초기 드래프트/폼 동기화가 끝나기 전 */
  isInitialSyncPending?: boolean;
}

export function WriteDailyLogFunnel({
  form,
  projectId,
  draftId,
  onNavigateBack,
  onSubmit,
  onStepChange,
  isSaving = false,
  lastUpdatedAt,
  isInitialSyncPending = false,
}: WriteDailyLogFunnelProps) {
  const { currentStep, currentIndex, totalSteps, goNext, goBack, hasNext, hasPrev, progress, setStep } = useFunnel(
    DAILY_LOG_STEPS,
    {
      initialStep: "Overview",
    },
  );
  const directionRef = useRef<"forward" | "back" | null>(null);
  const funnelRef = useRef<HTMLElement>(null);
  const imageController = useDailyLogImageController(form, { projectId, draftId });

  const handleNext = () => {
    if (!canProceedToNextStep(currentStep, form.state.values)) return;
    directionRef.current = "forward";
    goNext();
    const nextIndex = currentIndex + 1;
    if (nextIndex < totalSteps) {
      onStepChange?.(DAILY_LOG_STEPS[nextIndex] as DailyLogStep);
    }
  };

  const handleBack = () => {
    directionRef.current = "back";
    goBack();
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      onStepChange?.(DAILY_LOG_STEPS[prevIndex] as DailyLogStep);
    }
  };

  /**
   * desktop 단계 rail 클릭. 활성화 기준은 `getDailyLogRailStepStates`와 같다.
   * - 현재·이전 단계: 검증 없이 이동한다.
   * - 뒤 단계: 첫 단계부터 목표 직전까지 모두 유효해야 이동한다. 그 사이 값이 바뀌어 막히는 단계가
   *   생겼으면 그 단계로 이동해 채워야 할 곳을 보여 준다.
   */
  const handleRailStepSelect = (targetIndex: number) => {
    if (isInitialSyncPending || targetIndex === currentIndex) return;

    let destinationIndex = targetIndex;
    if (targetIndex > currentIndex) {
      const blockingIndex = findFirstBlockingStepIndex(DAILY_LOG_STEPS, 0, targetIndex, form.state.values);
      if (blockingIndex === currentIndex) return;
      if (blockingIndex !== null) destinationIndex = blockingIndex;
    }

    directionRef.current = destinationIndex > currentIndex ? "forward" : "back";
    const destinationStep = DAILY_LOG_STEPS[destinationIndex] as DailyLogStep;
    setStep(destinationStep);
    onStepChange?.(destinationStep);
  };

  useSwipeGesture(funnelRef, {
    onSwipeLeft: handleNext,
    onSwipeRight: handleBack,
    enabled: !isInitialSyncPending,
  });

  // PC 단계 rail을 sticky header 바로 아래에 고정하기 위해 header 높이를 CSS 변수로 넘긴다.
  // (초기 동기화 진행 바 유무 등으로 높이가 바뀌므로 고정값을 쓰지 않는다.)
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(function trackStickyHeaderHeight() {
    const header = headerRef.current;
    if (!header || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setHeaderHeight(entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height);
    });
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="flex flex-col min-h-dvh"
      style={{ "--write-header-height": `${headerHeight}px` } as React.CSSProperties}
    >
      <header ref={headerRef} className="sticky top-0 z-10 bg-gray-10">
        <div className="p-4 md:px-0">
          <button type="button" onClick={onNavigateBack} aria-label="Go back">
            <ChevronLeft width={24} height={24} className="text-gray-900 cursor-pointer" />
          </button>
        </div>
        <div className="flex justify-between md:justify-end items-end p-4 pt-0 md:px-0">
          {/* md+ 부터는 폼 카드 자체 헤더가 Step/Title을 시각적으로 보여 준다 — 이 h1은 접근성 트리 유지를 위해 sr-only로만 남긴다. */}
          <div className="md:sr-only">
            <p className="text-gray-800 text-label2">
              Step {currentIndex + 1}/{totalSteps}
            </p>
            <h1 className="mt-1 text-gray-900 text-title6">{STEP_TITLES[currentStep]}</h1>
          </div>
          {/* 저장 상태 자리의 높이를 "Last updated" 두 줄(caption1 × 2 ≈ 31px)만큼 항상 확보한다.
              없음 → Saving… → Last updated로 바뀌어도 아래 폼이 밀리지 않는다. */}
          <div className="flex justify-end items-end min-h-8">
            {isSaving ? (
              <div className="flex gap-1 items-center text-gray-500 text-caption1">
                <Spinner size={12} color="#7A7A7A" />
                Saving...
              </div>
            ) : (
              lastUpdatedAt && (
                <p className="flex flex-col text-right text-gray-500 text-caption1">
                  Last updated
                  <span>{dayjs(lastUpdatedAt).format("MMM D, YYYY h:mm A")}</span>
                </p>
              )
            )}
          </div>
        </div>
        {isInitialSyncPending ? (
          <div className="draft-sync-progress-bar md:max-w-[720px]" aria-hidden />
        ) : (
          // 1024px 이상은 왼쪽 단계 rail이 진행 상태를 보여 주므로 상단 진행 바를 숨긴다.
          <ProgressBar
            progress={progress}
            size="light"
            withAnimation
            className="bg-gray-300 md:max-w-[720px] lg:hidden"
          />
        )}
      </header>

      {/* PC: 하단 고정 액션 바(약 73px)에 카드 끝이 가려지지 않도록 바 높이 + 여유만큼 비운다.
          모바일은 폼 본문 자체의 하단 여백이 같은 역할을 한다. */}
      <div className="md:pb-[calc(env(safe-area-inset-bottom)+6rem)] lg:flex lg:gap-x-8 lg:items-start">
        {/* 입력에 따라 이동 가능 단계가 바로 바뀌도록 폼 값을 구독한다. */}
        <form.Subscribe selector={(state) => state.values}>
          {(values) => (
            <DailyLogStepRail
              stepStates={getDailyLogRailStepStates(DAILY_LOG_STEPS, currentIndex, values)}
              currentIndex={currentIndex}
              onSelectStep={handleRailStepSelect}
              disabled={isInitialSyncPending}
            />
          )}
        </form.Subscribe>

        <main
          ref={funnelRef}
          className="relative flex-1 min-w-0 overflow-x-hidden bg-white md:max-w-[720px] md:rounded-16 md:border md:border-gray-200 md:shadow-box"
          aria-busy={isInitialSyncPending}
        >
          {/* md+ 폼 카드 자체 헤더 — 모바일에서는 위 sticky header가 이 정보를 보여 주므로 숨긴다. */}
          <div className="hidden md:flex md:items-end md:justify-between md:gap-3 md:border-b md:border-gray-200 md:px-6 md:py-5">
            <div>
              <p className="text-gray-600 text-label3">
                Step {currentIndex + 1} / {totalSteps}
              </p>
              <p className="mt-1 text-gray-900 text-title6">{STEP_TITLES[currentStep]}</p>
            </div>
          </div>

          <div className="p-4 pb-[calc(env(safe-area-inset-bottom)+72px+16px)] md:px-6 md:py-6 md:pb-8">
            <div
              key={currentStep}
              className={directionRef.current ? `funnel-slide-${directionRef.current}` : undefined}
            >
              <Funnel step={currentStep}>
                <Funnel.Step name="Overview">
                  <OverviewStep form={form} />
                </Funnel.Step>
                <Funnel.Step name="Workforce">
                  <WorkforceStep form={form} />
                </Funnel.Step>
                <Funnel.Step name="Equipment">
                  <EquipmentStep form={form} />
                </Funnel.Step>
                <Funnel.Step name="MilestoneReport">
                  <MilestoneReportStep form={form} />
                </Funnel.Step>
                <Funnel.Step name="SitePicture">
                  <SitePictureStep controller={imageController} />
                </Funnel.Step>
              </Funnel>
            </div>
          </div>
          {isInitialSyncPending && (
            <div
              className="absolute inset-0 z-[1] flex flex-col justify-center items-center px-6 text-center bg-white/70 backdrop-blur-[3px] pointer-events-auto"
              aria-live="polite"
            />
          )}
        </main>
      </div>

      <form.Subscribe selector={(state) => state.values}>
        {(values) => {
          const isNextDisabled = isInitialSyncPending || (hasNext && !canProceedToNextStep(currentStep, values));
          const isSubmitDisabled =
            isInitialSyncPending || (currentStep === "SitePicture" && !isSitePictureStepComplete(values));

          return (
            <FixedBottomCTA className="md:left-[260px] md:px-0 md:border-t md:border-gray-200">
              <div className="mx-auto w-full md:flex md:max-w-[1200px] md:items-center md:px-4 lg:gap-x-8 lg:px-8">
                <div className="hidden lg:block lg:w-[220px] lg:shrink-0" aria-hidden="true" />
                <div className="flex gap-x-2 md:max-w-[720px] md:flex-1 md:justify-end">
                  {hasPrev && (
                    <Button
                      variant="outline"
                      color="info"
                      onClick={handleBack}
                      className="flex-1 md:flex-none"
                      disabled={isInitialSyncPending}
                    >
                      Back
                    </Button>
                  )}
                  {hasNext ? (
                    <Button onClick={handleNext} color="info" className="flex-1 md:flex-none" disabled={isNextDisabled}>
                      Next
                    </Button>
                  ) : (
                    <Button
                      onClick={onSubmit}
                      color="info"
                      className="flex-1 md:flex-none"
                      disabled={isInitialSyncPending || isSubmitDisabled}
                    >
                      Save
                    </Button>
                  )}
                </div>
              </div>
            </FixedBottomCTA>
          );
        }}
      </form.Subscribe>
    </div>
  );
}
